import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { type DateRange as DateRangeType } from 'react-day-picker';
import { capitalize } from '@recargas-dominicanas/core/utils';
import { getDateRangeOptions } from '@recargas-dominicanas/core/constants';
import { MetricCard } from '@recargas-dominicanas/core/components';
import { useSalesStore } from '../../stores/salesStore';

import { differenceInMonths } from 'date-fns';

import {
  PageContainer,
  PageContent,
  PageToolbar,
  Title,
  OutlineCard,
  BarChart,
  AreaChart,
  DateRange
} from '@recargas-dominicanas/core/components';

import { style } from './sales.module.css';

export function Sales() {

  const salesStore = useSalesStore();

  const options = useMemo(getDateRangeOptions, []);

  const [dateRange, setDateRange] = useState<DateRangeType>(options[0]);
  const [period, setPeriod] = useState<'day' | 'month'>('day');;
  const [fetching, setFetching] = useState(false);

  const loading = salesStore.loading && salesStore.transactions.length === 0;

  const sales = useMemo(() => salesStore.transactions.map(({ date, sales }) => ({ 
    x: new Date(date), 
    y: { sales } 
  })), [salesStore.transactions]);

  const profits = useMemo(() => salesStore.transactions.map(({ date, profit }) => ({ 
    x: new Date(date), 
    y: { profit }
  })), [salesStore.transactions]);

  const salesByProduct = useMemo(() => salesStore.transactionsByProduct.map(({ date, ...item }) => ({ 
    x: new Date(date), 
    y: item
  })), [salesStore.transactionsByProduct]);

  useEffect(() => {
    handleRefetch();
  }, []);

  async function handleDateRangeChange(dateRange: DateRangeType) {

    const period = dateRange.from && dateRange.to 
      ? differenceInMonths(dateRange.to, dateRange.from) < 4 ? 'day' : 'month'
      : 'day';

    setDateRange(dateRange);    
    setPeriod(period);
    
    setFetching(true);
    await salesStore.fetch(dateRange.from, dateRange.to, period);
    setFetching(false);
  }

  async function handleRefetch() {
    await salesStore.fetch(dateRange.from, dateRange.to, period);
  }

  function formatDate(date: Date) {
    return period === 'day'
      ? date.toLocaleDateString('es-DO', { dateStyle: 'medium' })
      : capitalize(date.toLocaleDateString('es-DO', { month: 'short', year: 'numeric' }));
  }

  function formatCurrency(value: number) {
    const formattedNumber = new Intl.NumberFormat('es-DO').format(value);
    return `RD$ ${formattedNumber}`;
  }

  function formatNumber(value: number) {
    return new Intl.NumberFormat('es-DO').format(value);
  }

  return (
    <PageContainer className={style.container}>
      <PageToolbar className={style.toolbar}>
        <Title style={style.title} title='Ventas' />
        <DateRange
          value={dateRange}
          options={options}
          onChange={handleDateRangeChange}/>
      </PageToolbar>
      <PageContent 
        className={clsx(style.content, fetching && style.contentDisabled)}>
        {!salesStore.failed &&
          <Fragment>
            <div className={style.metricsRow}>
              <MetricCard 
                icon="account_balance_wallet" 
                label="Balance" 
                value={formatCurrency(salesStore.balance)}
                color="emerald"
                loading={loading}/>
              <MetricCard 
                icon="trending_up" 
                label="Ventas" 
                value={formatCurrency(salesStore.sales)}
                color="emerald"
                loading={loading}/>
              <MetricCard 
                icon="payments" 
                label="Beneficio" 
                value={formatCurrency(salesStore.profit)}
                color="emerald"
                loading={loading}/>
              <MetricCard 
                icon="receipt_long" 
                label="Transacciones" 
                value={formatNumber(salesStore.count)}
                color="blue"
                loading={loading}/>
            </div>
            <div className={style.chartsContainer}>
              <div className={style.topCharts}>
                <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
                  <div className={style.chartTitle}>Ventas</div>
                  {sales.length > 0 &&
                    <BarChart
                      data={sales}
                      formatX={formatDate}
                      formatY={formatCurrency}
                      bars={[{ 
                        key: 'sales', 
                        title: 'Ventas', 
                        color: 'var(--color-emerald-500)'
                      }]}/>
                  }
                  {!sales.length &&
                    <div className={style.empty}>No hay datos para mostrar</div>
                  }
                </OutlineCard>
                <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
                  <div className={style.chartTitle}>Beneficios</div>
                  {profits.length > 0 &&
                    <BarChart
                      data={profits}
                      formatX={formatDate}
                      formatY={formatCurrency}
                      bars={[{ 
                        key: 'profit', 
                        title: 'Beneficios', 
                        color: 'var(--color-emerald-500)'
                      }]}/>
                  }
                  {!profits.length &&
                    <div className={style.empty}>No hay datos para mostrar</div>
                  }
                </OutlineCard>
              </div>
              <div className={style.bottomChart}>
                <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
                  <div className={style.chartTitle}>Ventas por producto</div>
                  {salesByProduct.length > 0 && 
                    <AreaChart
                      data={salesByProduct}
                      showLegend={true}
                      formatX={formatDate}
                      formatY={formatCurrency}
                      areas={[
                        { key: 'pin', title: 'Pines', color: 'var(--color-amber-500)', fillColor: 'var(--color-amber-400)' },
                        { key: 'invoice', title: 'Facturas', color: 'var(--color-blue-500)', fillColor: 'var(--color-blue-400)' },
                        { key: 'dataPlan', title: 'Paqueticos', color: 'var(--color-purple-500)', fillColor: 'var(--color-purple-400)' },
                        { key: 'topup', title: 'Recargas', color: 'var(--color-emerald-500)', fillColor: 'var(--color-emerald-400)' },
                        { key: 'insurance', title: 'Seguros', color: 'var(--color-orange-500)', fillColor: 'var(--color-emerald-400)' },
                      ]}/>
                  }
                  {!salesByProduct.length &&
                    <div className={style.empty}>No hay datos para mostrar</div>
                  }
                </OutlineCard>
              </div>
            </div>
          </Fragment>
        }
      </PageContent>
    </PageContainer>
  );
}
